import { type ReactElement, type ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "@/components/AppShell/AppShell";
import { AuthGate } from "@/components/Auth/AuthGate";
import { LoginPage } from "@/components/Auth/LoginPage";
import { BrokerConnectPage } from "@/components/Broker/BrokerConnectPage";
import { DashboardPage } from "@/components/Dashboard/DashboardPage";
import { MarketPage } from "@/components/Market/MarketPage";
import { ReportsPage } from "@/components/Reports/ReportsPage";
import { StrategyReportPage } from "@/components/Reports/StrategyReportPage";
import { StrategiesPage } from "@/components/Strategies/StrategiesPage";
import { StrategyDetailPage } from "@/components/Strategies/StrategyDetailPage";
import { BackupPage } from "@/components/Backup/BackupPage";
import { SimulatorPage } from "@/components/Simulator/SimulatorPage";
import { TradeLogPage } from "@/components/TradeLog/TradeLogPage";
import { makeQueryClient } from "@/lib/queryClient";

const queryClient = makeQueryClient();

/** Full-screen page WITHOUT the app header/nav — the strategy cockpit
 * and the report pages open in their own tabs and live alone there
 * (operator request 2026-07-02/03). */
function Standalone({ children }: Readonly<{ children: ReactNode }>): ReactElement {
  return <div className="min-h-screen bg-surface-0">{children}</div>;
}

/** Everything behind the session gate. */
function AuthedRoutes(): ReactElement {
  return (
    <Routes>
      <Route
        path="/strategies/:id"
        element={
          <Standalone>
            <StrategyDetailPage />
          </Standalone>
        }
      />
      <Route
        path="/reports"
        element={
          <Standalone>
            <ReportsPage />
          </Standalone>
        }
      />
      <Route
        path="/reports/:strategyId"
        element={
          <Standalone>
            <StrategyReportPage />
          </Standalone>
        }
      />
      <Route
        path="/*"
        element={
          <AppShell>
            <Routes>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/market" element={<MarketPage />} />
              <Route path="/strategies" element={<StrategiesPage />} />
              <Route path="/broker" element={<BrokerConnectPage />} />
              <Route path="/trade-log" element={<TradeLogPage />} />
              <Route path="/backup" element={<BackupPage />} />
              <Route path="/simulator" element={<SimulatorPage />} />
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </AppShell>
        }
      />
    </Routes>
  );
}

export function App(): ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* /login is rendered OUTSIDE AuthGate so unauthed users can reach it. */}
          <Route path="/login" element={<LoginPage />} />
          {/* Everything else requires a session (P5.2). */}
          <Route
            path="/*"
            element={
              <AuthGate>
                <AuthedRoutes />
              </AuthGate>
            }
          />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
